var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import format from 'string-format';
import { OGButtonLink } from '@opengear/buttons';
import { OGTable } from '@opengear/table';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { useOpen } from '@/hooks/open';
import { usePaginationModel } from '@/hooks/pagination-model';
import { IQuerySmfResourceField } from '../../../types/query/types';
import SmfResourceServiceButton from './smf-resource-service-button';
import { useSmfResources } from '@/api/smf-resources';
import { useSortModel } from '@/hooks/sort-model';
import { SmfResourceBulkOperations } from './smf-resource-bulk-operations';
import { SmfResourceDeleteModal } from './smf-resource-delete-modal';
import { SmfResourceSSHUrlModal } from './smf-resource-ssh-url-modal';
import { UpdateSmfResourceModal } from './smf-resource-modal';
import { ChipPopover } from '@/components/chip-popover';
import { useUIPermissions } from '@/api/sessions/self/ui';
import { useResourceTagsNames } from '@/api/tags/resource-tags/names';
import { smfResourceConnectionStatusToQuery, useJson } from '@/utils/query';
import { FreeTextSearch, useFreeTextSearch } from '@/components/free-text-search';
import { useDeviceView } from '@/hooks/use-device-view';
import { ResourceFilter, useResourceFilterSearchParams } from '@/containers/filters/advanced-filters/resource-filter';
import { formatDateTimestamp } from '@/utils/datetime';
import { DATETIME_FORMAT } from '@/constants/formats';
var SMF_RESOURCE_SERVICE_ORDER = ['ssh', 'http', 'https'];
var freeTextSearchFieldNames = [IQuerySmfResourceField.address, IQuerySmfResourceField.name];
export function SmfResourcesTable(_a) {
    var permissions = _a.permissions;
    var t = useTranslation().t;
    var refocusRef = useRef(null);
    var isMobileView = useDeviceView().isMobileView;
    var _b = useUIPermissions(), username = _b.username, isLoadingPermissions = _b.isLoadingPermissions;
    var currentUser = !isLoadingPermissions && username ? username : '';
    var _c = __read(useState([]), 2), rowSelectionModel = _c[0], setRowSelectionModel = _c[1];
    var tagNamesData = useResourceTagsNames({ order_by: 'name' }).data;
    var resourceTags = tagNamesData === null || tagNamesData === void 0 ? void 0 : tagNamesData.resourceTags;
    // Query parameters
    var _d = __read(usePaginationModel('smf_resources'), 3), paginationModel = _d[0], handlePaginationModelChange = _d[1], pageSizeOptions = _d[2];
    var _e = __read(useSortModel('name'), 3), defaultSortModel = _e[0], sortQueryParams = _e[1], handleSortModelChange = _e[2];
    var connectionStatusOptions = useMemo(function () { return [
        { value: 'any', name: t('pages.smf_resources.table.connection_status.any') },
        { value: 'connected', name: t('pages.smf_resources.table.connection_status.connected') },
        { value: 'disconnected', name: t('pages.smf_resources.table.connection_status.disconnected') },
    ]; }, [t]);
    // Connection status search
    var _f = __read(useState('any'), 2), connectionStatusFilterValue = _f[0], setConnectionStatusFilterValue = _f[1];
    // Free text search
    var _g = useFreeTextSearch(freeTextSearchFieldNames), textQueryObject = _g.textQueryObject, dispatchTextQuery = _g.dispatchTextQuery;
    // Resource filter
    var _h = useResourceFilterSearchParams(permissions), resourceFilter = _h.resourceFilter, setResourceFilter = _h.setResourceFilter;
    var connectionStatusQuery = useMemo(function () {
        if (connectionStatusFilterValue !== 'any') {
            return smfResourceConnectionStatusToQuery(connectionStatusFilterValue);
        }
        return null;
    }, [connectionStatusFilterValue]);
    var handleConnectionStatusFilterChange = useCallback(function (event) {
        var eventValue = event.target.value;
        setConnectionStatusFilterValue(eventValue);
    }, []);
    var json = useJson(resourceFilter === null || resourceFilter === void 0 ? void 0 : resourceFilter.queryObject, connectionStatusQuery, textQueryObject);
    var queryParams = useMemo(function () { return (__assign({ page: paginationModel.page + 1, per_page: paginationModel.pageSize, json: json }, sortQueryParams)); }, [paginationModel, json, sortQueryParams]);
    var _j = useSmfResources(queryParams), data = _j.data, isLoading = _j.isLoading;
    var _k = useMemo(function () { return ({
        loading: isLoading,
        rowCount: data === null || data === void 0 ? void 0 : data.meta.total_count,
        rows: (data === null || data === void 0 ? void 0 : data.smart_management_fabric_resources.map(function (smfResource) { return (__assign(__assign({}, smfResource), { resourceTagNames: smfResource.resource_tags.map(function (tag_id) {
                var smfResourceTag = tagNamesData === null || tagNamesData === void 0 ? void 0 : tagNamesData.resourceTags.find(function (tag) { return tag.id === tag_id; });
                return smfResourceTag ? { id: smfResourceTag.id, resource_tag_name: smfResourceTag.resource_tag_name } : { id: tag_id, resource_tag_name: tag_id };
            }) })); })) || [],
    }); }, [isLoading, data === null || data === void 0 ? void 0 : data.meta.total_count, data === null || data === void 0 ? void 0 : data.smart_management_fabric_resources, tagNamesData]), loading = _k.loading, rowCount = _k.rowCount, rows = _k.rows;
    var handleRowSelectionReset = useCallback(function () {
        setRowSelectionModel([]);
    }, []);
    var updateSmfResourceOpen = useOpen();
    var _l = __read(useState(null), 2), editSmfResource = _l[0], setEditSmfResource = _l[1];
    var handleDeleteSmfResource = useCallback(function () {
        setEditSmfResource(null);
    }, [setEditSmfResource]);
    var handleResourceNameClick = useCallback(function (row) {
        setEditSmfResource(row);
        updateSmfResourceOpen.handleOpen();
    }, [updateSmfResourceOpen]);
    var smfResourceSSHUrlOpen = useOpen();
    var _m = __read(useState({
        resourceName: '',
        sshUrl: '',
    }), 2), smfResourceSSHData = _m[0], setSmfResourceSSHData = _m[1];
    var handleClearSmfResourceSSHUrl = useCallback(function () {
        setSmfResourceSSHData({ resourceName: '', sshUrl: '' });
    }, [setSmfResourceSSHData]);
    var handleSmfResourceSSHUrlClick = useCallback(function (name, url) {
        setSmfResourceSSHData({ resourceName: name, sshUrl: url });
        smfResourceSSHUrlOpen.handleOpen();
    }, [setSmfResourceSSHData, smfResourceSSHUrlOpen]);
    var renderNameCell = useCallback(function (_a) {
        var row = _a.row, value = _a.value, tabIndex = _a.tabIndex;
        return (_jsx(OGButtonLink, __assign({ tabIndex: tabIndex, variant: "standard", onClick: function () {
                handleResourceNameClick(row);
            }, propagateSpacebar: false }, { children: value })));
    }, [handleResourceNameClick]);
    var renderConnectionCell = useCallback(function (_a) {
        var _b = _a.row, resourceName = _b.name, services = _b.services, tabIndex = _a.tabIndex;
        // Render empty cell if no services
        if (!services)
            return null;
        var resourceButtons = Object.entries(services)
            .sort(function (_a, _b) {
            var _c = __read(_a, 1), a = _c[0];
            var _d = __read(_b, 1), b = _d[0];
            return SMF_RESOURCE_SERVICE_ORDER.indexOf(a) - SMF_RESOURCE_SERVICE_ORDER.indexOf(b);
        }) // Reorder services
            .map(function (_a) {
            var _b = __read(_a, 2), serviceName = _b[0], service = _b[1];
            var url = service.url, status = service.connection_status, lastUpdated = service.connection_status_last_updated;
            var disabled = !(url === null || url === void 0 ? void 0 : url.length) || status === 'disconnected';
            var lastConnectivityCheck = lastUpdated !== undefined && lastUpdated !== 0 ? formatDateTimestamp(lastUpdated, DATETIME_FORMAT) : 'Pending';
            if (lastUpdated !== undefined && lastUpdated !== 0 && status === 'disconnected') {
                lastConnectivityCheck = 'Failure';
            }
            var toolTip = format(t('pages.smf_resources.table.tooltips.last_connectivity_check'), lastConnectivityCheck);
            var onClickHandler = serviceName === 'ssh' ? handleSmfResourceSSHUrlClick : undefined;
            return (_jsx(Box, __assign({ sx: { display: 'flex', gap: '1' } }, { children: _jsx(SmfResourceServiceButton, { resourceName: resourceName, serviceName: serviceName, url: url, toolTipTitle: toolTip, tabIndex: tabIndex, disabled: disabled, onClick: onClickHandler }) }), serviceName));
        });
        return (_jsx(Stack, __assign({ direction: "row", spacing: 1, minHeight: "21px" }, { children: resourceButtons })));
    }, [handleSmfResourceSSHUrlClick, t]);
    var columns = useMemo(function () { return [
        {
            field: 'name',
            flex: 1,
            minWidth: 150,
            headerName: t('pages.smf_resources.table.headers.name'),
            sortable: true,
            renderCell: renderNameCell,
        },
        {
            field: 'address',
            flex: 1,
            minWidth: 110,
            headerName: t('pages.smf_resources.table.headers.address'),
            sortable: true,
        },
        {
            field: 'resourceTagNames',
            flex: 2,
            minWidth: 240,
            headerName: t('pages.smf_resources.table.headers.tags'),
            sortable: false,
            renderCell: function (_a) {
                var value = _a.value, tabIndex = _a.tabIndex;
                if (value === null || value === void 0 ? void 0 : value.length) {
                    return _jsx(ChipPopover, { tags: value, tabIndex: tabIndex });
                }
                return null;
            },
        },
        {
            field: 'connections',
            flex: 1,
            minWidth: 150,
            headerName: t('pages.smf_resources.table.headers.connections'),
            sortable: false,
            renderCell: renderConnectionCell,
        },
    ]; }, [renderConnectionCell, renderNameCell, t]);
    return (_jsxs(_Fragment, { children: [_jsx(SmfResourceSSHUrlModal, { useOpenModel: smfResourceSSHUrlOpen, onClose: handleClearSmfResourceSSHUrl, currentUser: currentUser, resourceName: smfResourceSSHData.resourceName, sshUrl: smfResourceSSHData.sshUrl }), _jsxs(Box, __assign({ display: "flex", flexWrap: "wrap", marginBottom: 1.5, gap: 1.5 }, { children: [_jsx(ResourceFilter, { filter: resourceFilter, setFilter: setResourceFilter, permissions: { hasSmartgroupsEditAccess: permissions.hasSmartgroupsEditAccess, hasSmartgroupsViewAccess: permissions.hasSmartgroupsViewAccess } }), _jsx(Select, __assign({ onChange: handleConnectionStatusFilterChange, sx: { width: isMobileView ? '100%' : '300px', margin: 0 }, inputProps: { 'aria-label': t('pages.smf_resources.table.connection_status.label') }, value: connectionStatusFilterValue }, { children: connectionStatusOptions.map(function (option) { return (_jsx(MenuItem, __assign({ value: option.value }, { children: option.name }), option.name)); }) })), _jsx(Box, { flexGrow: 1 }), _jsx(FreeTextSearch, { ref: refocusRef, onSearch: dispatchTextQuery, useDebounce: false, translations: { tooltip: t('components.nodes.text_search.tooltip') } })] })), _jsx(SmfResourceBulkOperations, __assign({ onClose: handleRowSelectionReset, open: permissions && rowSelectionModel.length > 0, selectedCount: rowSelectionModel.length, totalCount: rows.length }, { children: _jsx(SmfResourceDeleteModal, { smfResourceIdsToAction: rowSelectionModel, disabled: !permissions.hasDeleteAccess, deleteButtonLabel: t('pages.smf_resources.table.buttons.bulk_delete_resources') }) })), editSmfResource && (_jsx(UpdateSmfResourceModal, { useOpenModel: updateSmfResourceOpen, permissions: permissions, smfResource: editSmfResource, onClose: handleDeleteSmfResource, resourceTagNames: resourceTags })), _jsx(OGTable, { id: "smf-resources-table", "aria-label": t('pages.smf_resources.table.label'), autoHeight: true, columns: columns, rows: rows, noRowsMessage: t('pages.smf_resources.table.no_row_message'), loading: loading, pageSizeOptions: pageSizeOptions, pagination: true, paginationMode: "server", paginationModel: paginationModel, onPaginationModelChange: handlePaginationModelChange, checkboxSelection: permissions.hasDeleteAccess, onRowSelectionModelChange: setRowSelectionModel, rowSelectionModel: rowSelectionModel, rowCount: rowCount, sortingMode: "server", onSortModelChange: handleSortModelChange, initialState: {
                    sorting: {
                        sortModel: defaultSortModel,
                    },
                } })] }));
}
